import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { CommonJobsStatus } from "../../../api/fraudElements";
import { selectGeneratedFraudElementsForParty } from "../logic/fraudElementsSlice";
import { getGeneratedFraudElements } from "../logic/fraudElementsThunk";
import GenerateFraudElementsEmptyState from "./GenerateFraudElementsEmptyState";
import GeneratedFraudElementsReadyState from "./GeneratedFraudElementsReadyState";
import GeneratingFraudElementsState from "./GeneratingFraudElementsState";
import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";

interface AIFraudElementsContainerProps {
  partyId: string;
}

const AIFraudElementsContainer: React.FC<AIFraudElementsContainerProps> = ({
  partyId,
}) => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const generatedFraudElements = useAppSelector((state) =>
    selectGeneratedFraudElementsForParty(state, partyId),
  );

  const [isLoading, setIsLoading] = useState(false);
  const status = generatedFraudElements?.status;

  useEffect(() => {
    if (
      !caseId ||
      status === CommonJobsStatus.AVAILABLE ||
      status === CommonJobsStatus.FAILED
    ) {
      return;
    }

    setIsLoading(true);

    dispatch(getGeneratedFraudElements({ case_id: caseId, party_id: partyId }))
      .unwrap()
      .catch(() => {
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, partyId, dispatch]);

  if (isLoading) {
    return (
      <div className="flex h-[574px] items-center justify-center rounded-b-[14px] rounded-t-none border border-[#E5E5E5] bg-white px-6 py-4">
        <CustomLoadingSpinner
          title="Fetching fraud elements..."
          subtitle="Please wait while we load fraud elements."
          className="m-0 h-full w-full border-0 shadow-none"
          minHeight="min-h-0"
        />
      </div>
    );
  }

  const isJobRunning =
    status === CommonJobsStatus.SUBMITTED || status === CommonJobsStatus.IN_PROGRESS;

  const hasGeneratedFraudElements =
    status === CommonJobsStatus.AVAILABLE &&
    (generatedFraudElements?.fraud_elements.length ?? 0) > 0;

  return (
    <div className="flex h-[574px] items-center justify-center rounded-b-[14px] rounded-t-none border border-[#E5E5E5] bg-white px-6 py-4">
      {isJobRunning && <GeneratingFraudElementsState partyId={partyId} />}

      {!isJobRunning && hasGeneratedFraudElements && (
        <GeneratedFraudElementsReadyState partyId={partyId} />
      )}

      {!isJobRunning && !hasGeneratedFraudElements && (
        <GenerateFraudElementsEmptyState partyId={partyId} />
      )}
    </div>
  );
};

export default AIFraudElementsContainer;